import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTribe, sendTribeMessage } from "../../../data/requests/tribe-requests";
import { getTribePosts, createTribePost, addReaction, removeReaction } from "../../../data/requests/tribe-post-requests";
import { getTribeTasks, joinTribeTask, leaveTribeTask, createTribeTask } from "../../../data/requests/tribe-task-requests";
import { getTribeHabits, joinTribeHabit, leaveTribeHabit, createTribeHabit } from "../../../data/requests/tribe-task-requests";
import { 
  Send, 
  ArrowLeft, 
  MessageSquare, 
  FileText, 
  CheckSquare, 
  Target, 
  Plus,
  Heart,
  MessageCircle,
  UserPlus,
  UserCheck,
  Calendar,
  Sparkles,
  Flame
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import AuthStore from "../../../stores/AuthStore";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";

export function TribeDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = AuthStore.user;
  const queryClient = useQueryClient();
  const scrollRef = useRef(null);
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // States
  const [activeTab, setActiveTab] = useState("chat");
  const [message, setMessage] = useState("");
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postTitle, setPostTitle] = useState("");
  const [postContent, setPostContent] = useState("");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [showCreateHabit, setShowCreateHabit] = useState(false);
  const [habitName, setHabitName] = useState("");
  const [habitDescription, setHabitDescription] = useState("");
  const [targetPerWeek, setTargetPerWeek] = useState(3);

  // Queries
  const { data: tribeResponse, isLoading } = useQuery({
    queryKey: ["tribe", id],
    queryFn: () => getTribe(id),
    refetchInterval: activeTab === "chat" ? 3000 : false,
  });

  const { data: postsResponse } = useQuery({
    queryKey: ["tribe", id, "posts"],
    queryFn: () => getTribePosts(id),
    enabled: activeTab === "posts",
  });

  const { data: tasksResponse } = useQuery({
    queryKey: ["tribe", id, "tasks"],
    queryFn: () => getTribeTasks(id),
    enabled: activeTab === "tasks",
  });

  const { data: habitsResponse } = useQuery({
    queryKey: ["tribe", id, "habits"],
    queryFn: () => getTribeHabits(id),
    enabled: activeTab === "habits",
  });

  const tribe = tribeResponse?.data?.data || tribeResponse?.data;
  const messages = tribe?.messages || [];
  const postsData = postsResponse?.data?.posts || postsResponse?.posts || postsResponse?.data || [];
  const posts = Array.isArray(postsData) ? postsData : [];
  const tasksData = tasksResponse?.data || tasksResponse || [];
  const tasks = Array.isArray(tasksData) ? tasksData : [];
  const habitsData = habitsResponse?.data || habitsResponse || [];
  const habits = Array.isArray(habitsData) ? habitsData : [];

  // Tribe statistics
  const memberCount = tribe?._count?.members || tribe?.members?.length || 0;
  const postCount = tribe?._count?.posts || posts.length || 0;
  const taskCount = tribe?._count?.tasks || tasks.length || 0;
  const habitCount = tribe?._count?.habits || habits.length || 0;

  // Debug: Log data to check structure
  if (tasks.length > 0) console.log('Tasks data:', tasks[0]);
  if (habits.length > 0) console.log('Habits data:', habits[0]);

  // Mutations
  const sendMessageMutation = useMutation({
    mutationFn: ({ id, content }) => sendTribeMessage(id, content),
    onSuccess: () => {
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ["tribe", id] });
    },
  });

  const createPostMutation = useMutation({
    mutationFn: (data) => createTribePost(id, data),
    onSuccess: () => {
      setShowCreatePost(false);
      setPostTitle("");
      setPostContent("");
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "posts"] });
      toast({
        title: t("common.success"),
        description: t("tribes.detail.posts.createSuccess", { defaultValue: "Gönderi başarıyla oluşturuldu!" }),
      });
    },
    onError: (error) => {
      const message = error.response?.data?.message || t("tribes.detail.posts.createError", { defaultValue: "Gönderi oluşturulamadı" });
      toast({
        title: t("common.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  // Add/Remove reaction mutation
  const reactionMutation = useMutation({
    mutationFn: ({ postId, emoji, hasReacted }) => {
      if (hasReacted) {
        return removeReaction(id, postId, emoji);
      } else {
        return addReaction(id, postId, emoji);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "posts"] });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("tribes.detail.posts.reactionError", { defaultValue: "Beğeni işlemi başarısız" }),
        variant: "destructive",
      });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => createTribeTask(id, data),
    onSuccess: () => {
      setShowCreateTask(false);
      setTaskTitle("");
      setTaskDescription("");
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "tasks"] });
    },
  });

  const createHabitMutation = useMutation({
    mutationFn: (data) => createTribeHabit(id, data),
    onSuccess: () => {
      setShowCreateHabit(false);
      setHabitName("");
      setHabitDescription("");
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "habits"] });
    },
  });

  const joinTaskMutation = useMutation({
    mutationFn: (taskId) => {
      console.log('Joining task:', taskId, 'in tribe:', id);
      return joinTribeTask(id, taskId);
    },
    onSuccess: () => {
      console.log('Successfully joined task');
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "tasks"] });
    },
    onError: (error) => {
      console.error('Error joining task:', error);
      alert('Failed to join task: ' + (error.response?.data?.message || error.message));
    },
  });

  const leaveTaskMutation = useMutation({
    mutationFn: (taskId) => {
      console.log('Leaving task:', taskId, 'in tribe:', id);
      return leaveTribeTask(id, taskId);
    },
    onSuccess: () => {
      console.log('Successfully left task');
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "tasks"] });
    },
    onError: (error) => {
      console.error('Error leaving task:', error);
      alert('Failed to leave task: ' + (error.response?.data?.message || error.message));
    },
  });

  const joinHabitMutation = useMutation({
    mutationFn: (habitId) => {
      console.log('Joining habit:', habitId, 'in tribe:', id);
      return joinTribeHabit(id, habitId);
    },
    onSuccess: () => {
      console.log('Successfully joined habit');
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "habits"] });
    },
    onError: (error) => {
      console.error('Error joining habit:', error);
      alert('Failed to join habit: ' + (error.response?.data?.message || error.message));
    },
  });

  const leaveHabitMutation = useMutation({
    mutationFn: (habitId) => {
      console.log('Leaving habit:', habitId, 'in tribe:', id);
      return leaveTribeHabit(id, habitId);
    },
    onSuccess: () => {
      console.log('Successfully left habit');
      queryClient.invalidateQueries({ queryKey: ["tribe", id, "habits"] });
    },
    onError: (error) => {
      console.error('Error leaving habit:', error);
      alert('Failed to leave habit: ' + (error.response?.data?.message || error.message));
    },
  });

  // Handlers
  const handleSend = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    sendMessageMutation.mutate({ id, content: message });
  };

  const handleCreatePost = () => {
    if (!postTitle.trim() || !postContent.trim()) return;
    createPostMutation.mutate({ title: postTitle, content: postContent });
  };

  const handleCreateTask = () => {
    if (!taskTitle.trim()) return;
    createTaskMutation.mutate({
      title: taskTitle,
      description: taskDescription,
      isUrgent: false,
      isImportant: false,
    });
  };

  const handleCreateHabit = () => {
    if (!habitName.trim()) return;
    createHabitMutation.mutate({
      name: habitName,
      description: habitDescription,
      targetPerWeek: parseInt(targetPerWeek),
    });
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current && activeTab === "chat") {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTab]);

  if (isLoading) {
    return (
      <AppLayout 
        title={t("tribes.detail.loading")} 
        loading={true}
      />
    );
  }
  
  if (!tribe) {
    return (
      <AppLayout title={t("tribes.detail.notFound")}>
        <div className="p-8 text-center">
          <p className="text-muted-foreground">{t("tribes.detail.notFound")}</p>
          <Button onClick={() => navigate("/tribes")} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("tribes.backToList")}
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={tribe.name}
      description={tribe.description || t("tribes.detail.description", { defaultValue: "Tribe community" })}
      actions={[
        {
          name: t("common.back"),
          icon: <ArrowLeft className="h-4 w-4" />,
          onClick: () => navigate("/tribes"),
        },
      ]}
    >
      <div className="space-y-6">
        {/* Tribe Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <UserPlus className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{memberCount}</p>
                  <p className="text-xs text-muted-foreground">{t("tribes.detail.stats.members", { defaultValue: "Members" })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <FileText className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{postCount}</p>
                  <p className="text-xs text-muted-foreground">{t("tribes.detail.stats.posts", { defaultValue: "Posts" })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <CheckSquare className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{taskCount}</p>
                  <p className="text-xs text-muted-foreground">{t("tribes.detail.stats.tasks", { defaultValue: "Tasks" })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Target className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{habitCount}</p>
                  <p className="text-xs text-muted-foreground">{t("tribes.detail.stats.habits", { defaultValue: "Habits" })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="chat" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              {t("tribes.detail.tabs.chat")}
            </TabsTrigger>
            <TabsTrigger value="posts" className="gap-2">
              <FileText className="h-4 w-4" />
              {t("tribes.detail.tabs.posts")}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <CheckSquare className="h-4 w-4" />
              {t("tribes.detail.tabs.tasks")}
            </TabsTrigger>
            <TabsTrigger value="habits" className="gap-2">
              <Target className="h-4 w-4" />
              {t("tribes.detail.tabs.habits")}
            </TabsTrigger>
            <TabsTrigger value="members" className="gap-2">
              <UserPlus className="h-4 w-4" />
              {t("tribes.detail.tabs.members")}
            </TabsTrigger>
          </TabsList>

          {/* Content Area */}
          <div className="mt-6">
            {/* Chat Tab */}
            <TabsContent value="chat" className="mt-0">
              <Card className="flex flex-col" style={{ height: "calc(100vh - 400px)", minHeight: "500px" }}>
                <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center py-12">
                      <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">{t("tribes.detail.chat.empty", { defaultValue: "No messages yet. Start the conversation!" })}</p>
                    </div>
                  ) : (
                    <>
                      {messages.slice().reverse().map((msg) => {
                        const isMe = msg.sender.id === user?.id;
                        return (
                          <div key={msg.id} className={cn("flex gap-3", isMe && "flex-row-reverse")}>
                            <Avatar className="h-10 w-10 shrink-0">
                              <AvatarFallback>{msg.sender.name?.charAt(0).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className={cn("flex flex-col gap-1 max-w-[70%]", isMe && "items-end")}>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="font-medium">
                                  {isMe
                                    ? t("tribes.detail.chat.you")
                                    : msg.sender.name}
                                </span>
                                <span>
                                  {msg.createdAt ? format(typeof msg.createdAt === 'string' ? parseISO(msg.createdAt) : new Date(msg.createdAt), "HH:mm") : ''}
                                </span>
                              </div>
                              <div
                                className={cn(
                                  "px-4 py-2.5 rounded-2xl text-sm break-words",
                                  isMe
                                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                                    : "bg-muted rounded-tl-sm"
                                )}
                              >
                                {msg.content}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={scrollRef} />
                    </>
                  )}
                </CardContent>
                <CardFooter className="border-t p-4 bg-card sticky bottom-0">
                  <form onSubmit={handleSend} className="flex gap-3 w-full">
                    <Input
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t("tribes.detail.chat.placeholder")}
                      className="flex-1"
                    />
                    <Button type="submit" size="icon" disabled={!message.trim() || sendMessageMutation.isPending}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </CardFooter>
              </Card>
            </TabsContent>

            {/* Posts Tab */}
            <TabsContent value="posts" className="mt-0">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">
                    {t("tribes.detail.posts.title")}
                  </h2>
                  <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("tribes.detail.posts.createButton")}
                      </Button>
                    </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t("tribes.detail.posts.createTitle")}
                    </DialogTitle>
                    <DialogDescription>
                      {t("tribes.detail.posts.createDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="post-title">
                        {t("tribes.detail.posts.fields.title")}
                      </Label>
                      <Input
                        id="post-title"
                        value={postTitle}
                        onChange={(e) => setPostTitle(e.target.value)}
                        placeholder={t(
                          "tribes.detail.posts.fields.titlePlaceholder"
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="post-content">
                        {t("tribes.detail.posts.fields.content")}
                      </Label>
                      <Textarea
                        id="post-content"
                        value={postContent}
                        onChange={(e) => setPostContent(e.target.value)}
                        placeholder={t(
                          "tribes.detail.posts.fields.contentPlaceholder"
                        )}
                        rows={6}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleCreatePost}
                      disabled={!postTitle.trim() || !postContent.trim()}
                    >
                      {t("tribes.detail.posts.createSubmit")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-4">
                  {posts.map((post) => (
                    <Card 
                  key={post.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/tribes/${id}/posts/${post.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{post.author?.name?.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{post.author?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {post.createdAt
                            ? format(
                                typeof post.createdAt === "string"
                                  ? parseISO(post.createdAt)
                                  : new Date(post.createdAt),
                                "dd.MM.yyyy HH:mm"
                              )
                            : ""}
                        </p>
                      </div>
                    </div>
                    <CardTitle className="text-lg">{post.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-wrap line-clamp-3">{post.content}</p>
                  </CardContent>
                  <CardFooter className="border-t pt-4">
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <button 
                        className={cn(
                          "flex items-center gap-1.5 hover:text-primary transition-colors",
                          post.reactions && post.reactions.length > 0 && "text-primary"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Backend returns only current user's reactions, so if there's any, user has reacted
                          const hasReacted = post.reactions && post.reactions.length > 0;
                          reactionMutation.mutate({ 
                            postId: post.id, 
                            emoji: "❤️",
                            hasReacted 
                          });
                        }}
                        disabled={reactionMutation.isPending}
                      >
                        <Heart className={cn(
                          "h-4 w-4",
                          post.reactions && post.reactions.length > 0 && "fill-current"
                        )} />
                        <span>{post._count?.reactions || 0}</span>
                      </button>
                      <button 
                        className="flex items-center gap-1.5 hover:text-primary transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tribes/${id}/posts/${post.id}`);
                        }}
                      >
                        <MessageCircle className="h-4 w-4" />
                        <span>{post._count?.comments || post.comments?.length || 0}</span>
                      </button>
                    </div>
                    </CardFooter>
                  </Card>
                  ))}
                      {posts.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>{t("tribes.detail.posts.empty")}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Tasks Tab */}
            <TabsContent value="tasks" className="mt-0">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">
                    {t("tribes.detail.tasks.title")}
                  </h2>
              <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    {t("tribes.detail.tasks.createButton")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t("tribes.detail.tasks.createTitle")}
                    </DialogTitle>
                    <DialogDescription>
                      {t("tribes.detail.tasks.createDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="task-title">
                        {t("tribes.detail.tasks.fields.title")}
                      </Label>
                      <Input
                        id="task-title"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        placeholder={t(
                          "tribes.detail.tasks.fields.titlePlaceholder"
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="task-description">
                        {t("tribes.detail.tasks.fields.description")}
                      </Label>
                      <Textarea
                        id="task-description"
                        value={taskDescription}
                        onChange={(e) => setTaskDescription(e.target.value)}
                        placeholder={t(
                          "tribes.detail.tasks.fields.descriptionPlaceholder"
                        )}
                        rows={4}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      onClick={handleCreateTask}
                      disabled={!taskTitle.trim()}
                    >
                      {t("tribes.detail.tasks.createSubmit")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
                </Dialog>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {tasks.map((task) => {
                const participants = task.tribeParticipants || task.participants || [];
                const isParticipant = task.isJoined || participants.some((p) => p.userId === user?.id);
                return (
                  <Card key={task.id}>
                    <CardHeader>
                      <CardTitle className="text-base">{task.title}</CardTitle>
                      {task.description && (
                        <CardDescription className="line-clamp-2">{task.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardFooter className="flex justify-between items-center border-t pt-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <UserCheck className="h-4 w-4" />
                        <span>
                          {t("tribes.detail.tasks.joinedCount", {
                            count:
                              task._count?.tribeParticipants ||
                              participants.length ||
                              0,
                          })}
                        </span>
                      </div>
                      {isParticipant ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => leaveTaskMutation.mutate(task.id)}
                          disabled={leaveTaskMutation.isPending}
                        >
                          {leaveTaskMutation.isPending
                            ? t("tribes.detail.tasks.leaving")
                            : t("tribes.detail.tasks.leave")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => joinTaskMutation.mutate(task.id)}
                          disabled={joinTaskMutation.isPending}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          {joinTaskMutation.isPending
                            ? t("tribes.detail.tasks.joining")
                            : t("tribes.detail.tasks.join")}
                        </Button>
                      )}
                    </CardFooter>
                    </Card>
                  );
                })}
                  {tasks.length === 0 && (
                  <div className="col-span-2 text-center py-12 text-muted-foreground">
                    <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t("tribes.detail.tasks.empty")}</p>
                  </div>
                )}
              </div>
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="mt-0">
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">
                  {t("tribes.detail.members.title", { defaultValue: "Tribe Members" })}
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tribe.members && tribe.members.length > 0 ? (
                  tribe.members.map((member) => {
                    const memberUser = member.user || member;
                    return (
                      <Card key={memberUser.id} className="hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback>
                                {memberUser.name?.charAt(0).toUpperCase() || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <CardTitle className="text-base">{memberUser.name || memberUser.email}</CardTitle>
                              {memberUser.email && (
                                <CardDescription className="text-xs">
                                  {memberUser.email}
                                </CardDescription>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        {memberUser.level && (
                          <CardContent>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {memberUser.level && (
                                <div className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  <span>{t("tribes.detail.members.level", { level: memberUser.level, defaultValue: `Level ${memberUser.level}` })}</span>
                                </div>
                              )}
                              {memberUser.streak !== undefined && memberUser.streak > 0 && (
                                <div className="flex items-center gap-1">
                                  <Flame className="h-4 w-4 text-orange-500" />
                                  <span>{memberUser.streak}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t("tribes.detail.members.empty", { defaultValue: "No members yet" })}</p>
                  </div>
                )}
                </div>
              </div>
            </TabsContent>

            {/* Habits Tab */}
            <TabsContent value="habits" className="mt-0">
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold">
                    {t("tribes.detail.habits.title")}
                  </h2>
                  <Dialog open={showCreateHabit} onOpenChange={setShowCreateHabit}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        {t("tribes.detail.habits.createButton")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {t("tribes.detail.habits.createTitle")}
                        </DialogTitle>
                        <DialogDescription>
                          {t("tribes.detail.habits.createDescription")}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="habit-name">
                            {t("tribes.detail.habits.fields.name")}
                          </Label>
                          <Input
                            id="habit-name"
                            value={habitName}
                            onChange={(e) => setHabitName(e.target.value)}
                            placeholder={t(
                              "tribes.detail.habits.fields.namePlaceholder"
                            )}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="habit-description">
                            {t("tribes.detail.habits.fields.description")}
                          </Label>
                          <Textarea
                            id="habit-description"
                            value={habitDescription}
                            onChange={(e) => setHabitDescription(e.target.value)}
                            placeholder={t(
                              "tribes.detail.habits.fields.descriptionPlaceholder"
                            )}
                            rows={3}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target-per-week">
                            {t("tribes.detail.habits.fields.targetPerWeek")}
                          </Label>
                          <Input
                            id="target-per-week"
                            type="number"
                            min="1"
                            max="7"
                            value={targetPerWeek}
                            onChange={(e) => setTargetPerWeek(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          onClick={handleCreateHabit}
                          disabled={!habitName.trim()}
                        >
                          {t("tribes.detail.habits.createSubmit")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {habits.map((habit) => {
                    const participants = habit.tribeParticipants || habit.participants || [];
                    const isParticipant = habit.isJoined || participants.some((p) => p.userId === user?.id);
                    return (
                      <Card key={habit.id}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-base">{habit.name}</CardTitle>
                            <Badge variant="secondary">
                              {t("tribes.detail.habits.perWeek", {
                                count: habit.targetPerWeek,
                              })}
                            </Badge>
                          </div>
                          {habit.description && (
                            <CardDescription className="line-clamp-2">{habit.description}</CardDescription>
                          )}
                        </CardHeader>
                        <CardFooter className="flex justify-between items-center border-t pt-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <UserCheck className="h-4 w-4" />
                            <span>
                              {t("tribes.detail.habits.joinedCount", {
                                count:
                                  habit._count?.tribeParticipants ||
                                  participants.length ||
                                  0,
                              })}
                            </span>
                          </div>
                          {isParticipant ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => leaveHabitMutation.mutate(habit.id)}
                              disabled={leaveHabitMutation.isPending}
                            >
                              {leaveHabitMutation.isPending
                                ? t("tribes.detail.habits.leaving")
                                : t("tribes.detail.habits.leave")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => joinHabitMutation.mutate(habit.id)}
                              disabled={joinHabitMutation.isPending}
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              {joinHabitMutation.isPending
                                ? t("tribes.detail.habits.joining")
                                : t("tribes.detail.habits.join")}
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                  {habits.length === 0 && (
                    <div className="col-span-2 text-center py-12 text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>{t("tribes.detail.habits.empty")}</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </AppLayout>
  );
}
